import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getTimesheets } from "@/api/timesheets";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2 } from "lucide-react";

export default function TimesheetsTable({ filters, additionalColumns = [] }) {
    const [timesheets, setTimesheets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [signatureImage, setSignatureImage] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 100;

    useEffect(() => {
        setCurrentPage(1); // Reset to page 1 on filter change
    }, [filters]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const data = await getTimesheets(filters);
            console.log(data)
            if (Array.isArray(data)) setTimesheets(data);
            setLoading(false);
        };
        fetchData();
    }, [filters]);

    const formatJobDate = (dateStr) => {
        if (!dateStr) return "—";
        const date = new Date(dateStr);
        const day = date.toLocaleDateString("en-GB", { weekday: "short" });
        const shortDate = date.toLocaleDateString("en-GB");
        return (
            <span className="block md:inline">
                <span className="hidden md:inline">{day} </span>{shortDate}
            </span>
        );
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return "—";
        const date = new Date(timeStr);
        return date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
        });
    };

    const pillLink = (to, text) => (
        <Link
            to={to}
            className="inline-block px-3 py-1 text-xs bg-[#f2e9ec] text-[#5D1F42] rounded-full hover:bg-[#e3d4da] transition"
        >
            {text}
        </Link>
    );

    const paginatedTimesheets = timesheets.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const totalPages = Math.ceil(timesheets.length / itemsPerPage);

    return (
        <div className="overflow-auto">
            {loading ? (
                <div className="flex justify-center items-center py-10">
                    <Loader2 className="animate-spin h-8 w-8 text-[#5D1F42]" />
                </div>
            ) : (
                <>
                    <table className="min-w-full border border-gray-200 text-sm rounded-xl overflow-hidden">
                        <thead className="bg-gray-50 text-left text-gray-700 uppercase text-xs tracking-wider">
                        <tr>
                            <th className="px-4 py-3 border-b">Date</th>
                            {additionalColumns.includes("venue") && <th className="px-4 py-3 border-b">Venue</th>}
                            {additionalColumns.includes("job") && <th className="px-4 py-3 border-b">Job</th>}
                            {additionalColumns.includes("candidate") && <th className="px-4 py-3 border-b">Candidate</th>}
                            <th className="px-4 py-3 border-b">Clocked In</th>
                            <th className="px-4 py-3 border-b">Clocked Out</th>
                            <th className="px-4 py-3 border-b">Signature</th>
                            <th className="px-4 py-3 border-b">Signed By</th>
                            <th className="px-4 py-3 border-b">Signed At</th>
                            <th className="px-4 py-3 border-b">Status</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        {paginatedTimesheets.map((ts) => (
                            <tr key={ts.id} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-2">{formatJobDate(ts.jobDate)}</td>
                                {additionalColumns.includes("venue") && (
                                    <td className="px-4 py-2">{pillLink(`/admin/venue/${ts.venueId}`, ts.venueName)}</td>
                                )}
                                {additionalColumns.includes("job") && (
                                    <td className="px-4 py-2">{pillLink(`/admin/jobs/${ts.jobId}`, ts.skillName)}</td>
                                )}
                                {additionalColumns.includes("candidate") && (
                                    <td className="px-4 py-2">{pillLink(`/admin/candidate/${ts.candidateSlugId}`, ts.candidateName)}</td>
                                )}
                                <td className="px-4 py-2">{formatTime(ts.clockIn)}</td>
                                <td className="px-4 py-2">{formatTime(ts.clockOut)}</td>
                                <td className="px-4 py-2">
                                    {ts.signMethod === "automatic" ? (
                                        <div className="flex items-center gap-2 text-green-600">
                                            <CheckCircle className="w-4 h-4" />
                                            <span>Approved by client</span>
                                        </div>
                                    ) : ts.signImage ? (
                                        <Button size="sm" onClick={() => setSignatureImage(ts.signImage)}>
                                            View Signature
                                        </Button>
                                    ) : (
                                        <span>—</span>
                                    )}
                                </td>
                                <td className="px-4 py-2">{ts.signedBy || "—"}</td>
                                <td className="px-4 py-2">{ts.signedAt || "—"}</td>
                                <td className="px-4 py-2">{ts.status || "—"}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex justify-between items-center mt-4">
                            <Button
                                variant="outline"
                                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </Button>
                            <span>
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </Button>
                        </div>
                    )}

                    <Dialog open={!!signatureImage} onOpenChange={() => setSignatureImage(null)}>
                        <DialogContent>
                            {signatureImage && (
                                <img src={signatureImage} alt="Signature" className="max-w-full" />
                            )}
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </div>
    );
}
